import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Switch } from '@/components/ui/switch'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import { CoiMode, type InsurancePolicy, setPolicyCoiMode } from '../../../../models/insurancePolicy'

/**
 * Whether certificates for this policy are generated by us or requested from a
 * Cai admin. Staff-only: the customer sees the consequence on the COI page, not
 * the switch. The caller gates on that (InsurancePolicy.canEditCoiMode) rather
 * than this component reading the user, so the policy card stays renderable
 * without a query client.
 */
export function PolicyCoiModeSetting({ policy }: { policy: InsurancePolicy }) {
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)

  const isAutomatic = (policy.coiMode ?? CoiMode.AUTOMATIC) === CoiMode.AUTOMATIC

  async function onCheckedChange(checked: boolean) {
    setSaving(true)
    try {
      await setPolicyCoiMode(policy.org, policy.id, checked ? CoiMode.AUTOMATIC : CoiMode.MANUAL)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, policy.org] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_DOCUMENT_LATEST] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.POST_BIND_CHECKLIST, policy.id] })
      toast.success(checked ? 'COIs will be generated automatically' : 'COIs will be requested from Cai')
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <label
      className="flex items-center gap-2 text-sm"
      title={
        isAutomatic
          ? 'We parse this policy and issue certificates ourselves.'
          : 'This carrier does not let us issue certificates: each holder is requested from a Cai admin, who uploads the document.'
      }
    >
      <Switch checked={isAutomatic} disabled={saving} onCheckedChange={onCheckedChange} />
      {isAutomatic ? 'Automatic COIs' : 'Manual COIs'}
    </label>
  )
}
